import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { TypographySection } from './TypographySection'
import { ComponentCatalogSection } from './ComponentCatalogSection'
import { Box, Button, Grid, InputLabel, Stack, Typography } from '@mui/material'
import { ComponentCatalogExample } from './ComponentCatalogExample'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { Chip } from 'components/Shared/Chip'
import { ItemDetailsBox } from 'components/Shared/ItemDetailsBox'
import { ColorSection } from './ColorSection'
import { DataTable } from 'components/Shared/DataTable'
import { NoDataIndicator } from 'components/NoDataIndicator/NoDataIndicator'
import { ImageCard } from 'components/ImageCard/ImageCard'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridColDef,
} from '@mui/x-data-grid'
import { dataTableIdFormatter } from 'utils/util'
import { useLayout } from 'hooks/useLayout'
import { TextField } from 'components/Shared/TextField'
import { Sublabel } from 'components/Sublabel'
import React from 'react'
import { HookableDateField } from 'components/Shared/HookableDateField'
import { HookableTimeField } from 'components/Shared/HookableTimeField'
import { Modal } from 'components/Modal/Modal'
import { SectionHeader } from 'components/SectionHeader'
import { ActivityButton } from 'components/ActivityButton'
import { colorPrimitives } from 'components/Theme'
import { useForm } from 'react-hook-form'

const DataTableWithCheckBoxesColumns = (isMobile: boolean): GridColDef[] => {
  return [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      valueFormatter: dataTableIdFormatter(isMobile),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      flex: 0.3,
    },
  ]
}

export const ComponentCatalogPage = () => {
  const { isMobile } = useLayout()

  const gridColumnSpacing = {
    xs: '24px',
    sm: '24px',
    md: '16px',
    lg: '24px',
    xl: '24px',
  }

  const dataTableWithCheckBoxesRows = [
    {
      name: 'Test 1',
      id: 1,
    },
    {
      name: 'Test 2',
      id: 2,
    },
  ]
  const [footerVisible, setFooterVisible] = React.useState(false)
  const [fullPageLoader, setFullPageLoader] = React.useState(false)
  const [modalVisible, setModalVisible] = React.useState(false)
  const [isActivityButtonActive, setIsActivityButtonActive] =
    React.useState(false)
  const [defaultValue, setDefaultValue] = React.useState(0)

  const { getValues } = useForm({
    defaultValues: {
      value: defaultValue,
    },
  })

  if (fullPageLoader) {
    return (
      <>
        <ActivityIndicator size="fullPage" />
        <Box
          bgcolor={colorPrimitives.redGaming}
          className="fixed top-3 left-0 px-5 py-2 cursor-pointer text-white"
          onClick={() => {
            setFullPageLoader(false)
          }}
        >
          <Typography>Click to close Full Page activity indicator</Typography>
        </Box>
      </>
    )
  }

  return (
    <Page
      header={<PageHeader title="Component Catalog" />}
      footer={
        footerVisible && (
          <>
            <Typography>Footer Bar</Typography>
            <Stack direction="row" gap={2}>
              <Button variant="outlined">Test Button 1</Button>
              <Button variant="contained">Test Button 2</Button>
            </Stack>
          </>
        )
      }
    >
      <ComponentCatalogSection name="Default Values">
        <Typography variant="body-1">{getValues('value')}</Typography>
        <Typography variant="body-1">{defaultValue}</Typography>
        <Button onClick={() => setDefaultValue(defaultValue + 1)}>
          Advance
        </Button>
      </ComponentCatalogSection>
      <TypographySection />
      <ColorSection />
      <ComponentCatalogSection name="Buttons">
        <Grid container rowSpacing="16px" columnSpacing={gridColumnSpacing}>
          <ComponentCatalogExample name={'variant="text"'}>
            <Button variant="text">Enfabulate Scrummox</Button>
          </ComponentCatalogExample>

          <ComponentCatalogExample name={'variant="contained"'}>
            <Button variant="contained">Tap Me</Button>
          </ComponentCatalogExample>

          <ComponentCatalogExample name={'variant="outlined"'}>
            <Button variant="outlined">Cancel</Button>
          </ComponentCatalogExample>
        </Grid>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="ActivityButton">
        <Grid container rowSpacing="16px" columnSpacing={gridColumnSpacing}>
          <ComponentCatalogExample name={'variant="text"'}>
            <ActivityButton
              variant="text"
              onClick={() => setIsActivityButtonActive(!isActivityButtonActive)}
              active={isActivityButtonActive}
            >
              Click to Toggle Progress
            </ActivityButton>
          </ComponentCatalogExample>

          <ComponentCatalogExample name={'variant="contained"'}>
            <ActivityButton
              variant="contained"
              onClick={() => setIsActivityButtonActive(!isActivityButtonActive)}
              active={isActivityButtonActive}
            >
              Click to Toggle Progress
            </ActivityButton>
          </ComponentCatalogExample>

          <ComponentCatalogExample name={'variant="outlined"'}>
            <ActivityButton
              variant="outlined"
              onClick={() => setIsActivityButtonActive(!isActivityButtonActive)}
              active={isActivityButtonActive}
            >
              Click to Toggle Progress
            </ActivityButton>
          </ComponentCatalogExample>
        </Grid>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Activity Indicators">
        <ComponentCatalogExample name="Small">
          <Box>
            <ActivityIndicator size="small" />
          </Box>
        </ComponentCatalogExample>
        <ComponentCatalogExample name="Medium">
          <Box>
            <ActivityIndicator size="medium" />
          </Box>
        </ComponentCatalogExample>
        <ComponentCatalogExample name="Big">
          <Box>
            <ActivityIndicator size="big" />
          </Box>
        </ComponentCatalogExample>
        <ComponentCatalogExample name="Full Page">
          <Box>
            <Button variant="contained" onClick={() => setFullPageLoader(true)}>
              Click to open Full Page activity indicator
            </Button>
          </Box>
        </ComponentCatalogExample>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Components">
        <Grid
          container
          rowSpacing="16px"
          columnSpacing={{
            xs: '24px',
            sm: '24px',
            md: '16px',
            lg: '24px',
            xl: '24px',
          }}
        >
          <ComponentCatalogExample name="Chip">
            <Chip label="Patron" />
            <Chip label="Licensed Establishment" />
            <Chip label="Active" />
          </ComponentCatalogExample>

          <ComponentCatalogExample name="ItemDetailsBox">
            <ItemDetailsBox
              boxTitle="Example Details"
              firstFieldTitle="A Primary Field"
              firstFieldText="The Kazbah"
              secondFieldTitle="A Secondary Field"
              secondFieldText="123"
            />
          </ComponentCatalogExample>

          <ComponentCatalogExample name="DataTable with checkBoxes">
            <DataTable
              columns={DataTableWithCheckBoxesColumns(isMobile)}
              rows={dataTableWithCheckBoxesRows}
              checkboxSelection
              hideFooter
            />
          </ComponentCatalogExample>

          <ComponentCatalogExample name="No Data Indicator">
            <NoDataIndicator />
          </ComponentCatalogExample>

          <ComponentCatalogExample name="Image Card">
            <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: 10 }}>
              <ImageCard
                title="title"
                subtitle="subtitle"
                description="description"
              />
              <ImageCard
                title="title"
                subtitle="subtitle"
                description="description"
                isSelected
              />
            </Box>
          </ComponentCatalogExample>
        </Grid>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Page Header">
        <SectionHeader
          title="An Example Page Header"
          onClickButton={() => alert('tee hee!')}
        />
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Footer">
        <Button
          variant="contained"
          onClick={() => setFooterVisible(!footerVisible)}
        >
          Toggle footer
        </Button>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Modal">
        <Button
          variant="contained"
          onClick={() => setModalVisible(!modalVisible)}
        >
          Toggle Modal Visible
        </Button>
      </ComponentCatalogSection>

      <ComponentCatalogSection name="Form Elements">
        <Stack gap={4}>
          <TextField
            label="Text Field"
            required
            sublabel={<Sublabel>Sublabel</Sublabel>}
            helperText={'Helper text'}
          />
          <Box>
            <InputLabel>Date Field</InputLabel>
            <HookableDateField onChange={async () => {}} />
          </Box>
          <Box>
            <InputLabel>Time Field</InputLabel>
            <HookableTimeField onChange={async () => {}} />
          </Box>
        </Stack>
      </ComponentCatalogSection>
      <ComponentCatalogSection name="Sentry">
        <Box>
          <InputLabel>Trigger Error</InputLabel>
          <Button
            variant="contained"
            onClick={() => {
              throw new Error('Explosion!')
            }}
          >
            Explode
          </Button>
        </Box>
      </ComponentCatalogSection>

      <Modal
        isOpen={modalVisible}
        toggleIsOpen={() => {
          setModalVisible(!modalVisible)
        }}
        sx={{
          height: '40vh',
          maxWidth: { xs: '90vw', sm: '40vw' },
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ p: '32px', textAlign: 'center' }}>
            Modal dimensions defined by content, Change modal dimensions passing
            sx props like:
          </Typography>

          <Typography
            sx={{ py: '32px', textAlign: 'center' }}
            fontWeight="bold"
          >
            {`<Modal ...props
              sx={{
               height: '40vh',
               maxWidth: { xs: '90vw', sm: '40vw' },
              }}
            >`}
          </Typography>
        </Box>
      </Modal>
    </Page>
  )
}
